import React, { ReactNode, useEffect } from "react";
import { useMeCore } from "../../cores/me";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContactCore } from "../../cores/contacts";
import { useIntl, FormattedMessage } from "react-intl";

import {
  faFileAlt,
  faClock,
  faRobot,
  faUserFriends,
  faBuilding,
  faChartLine,
  faQuestionCircle,
  faExternalLinkAlt,
  faBolt,
  faGem,
} from "@fortawesome/free-solid-svg-icons";
import { UpgradePlanButton } from "../component/plan/UpgradePlanButton";

/** Dashboard (/social/dashboard): métricas, acciones rápidas y plan. */
const HomeDashboard = () => {
  const { company, companyPlan, getMeSocialSettings, getMeUserSettings } =
    useMeCore();

  const intl = useIntl();
  const {
    getNewContactsCount,
    newContactsCount,
    newCompaniesCount,
    getCompaniesCount,
  } = useContactCore();

  useEffect(() => {
    getMeUserSettings();

    if (company) getMeSocialSettings(company.id);
  }, [getMeSocialSettings, getMeUserSettings, company]);

  useEffect(() => {
    getCompaniesCount(company?.id!);
    getNewContactsCount(company?.id!);
  }, [company?.id, getCompaniesCount, getNewContactsCount]);

  const quickActions = [
    {
      path: "/binding",
      icon: "circle-nodes",
      label: "Gestionar Integraciones",
      allowed: true,
    },
    {
      path: "/boards/business",
      icon: "people-robbery",
      label: "app.home.createOpportunity",
      allowed: companyPlan?.rules.allowBusinessBoard,
    },
    {
      path: "/boards/tasks",
      icon: "tasks",
      label: "app.home.createTask",
      allowed: companyPlan?.rules.allowBusinessBoard,
    },
    {
      path: "/calendar",
      icon: "calendar",
      label: "app.home.bookMeet",
      allowed: companyPlan?.rules.allowBusinessBoard,
    },
    {
      path: "/automations",
      icon: "robot",
      label: "app.home.automateProcess",
      allowed: companyPlan?.rules.allowAutomation,
    },
    {
      path: "/contacts",
      icon: "address-book",
      label: "app.home.createContacts",
      allowed: companyPlan?.rules.allowDatabase,
    },
    {
      path: "/companies/contact",
      icon: "city",
      label: "app.home.createEnterprise",
      allowed: companyPlan?.rules.allowDatabase,
    },
  ];

  return (
    <div className="home-container home-container--dashboard-only">
      <main className="dashboard-content dashboard-content--modern home-welcome-shell">
        <header className="dashboard-page-header">
          <span className="dashboard-page-eyebrow">
            <FontAwesomeIcon icon={faBolt} className="me-2" />
            <FormattedMessage id="app.home.intro.eyebrow" />
          </span>
          <h1 className="dashboard-page-title">
            <FormattedMessage id="app.homeDashboard.pageTitle" />
          </h1>
        </header>
        {/* Quick Stats */}
        <section className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faUserFriends} />
            </div>
            <div className="stat-info">
              <h3>+{newContactsCount}</h3>
            </div>
            <div className="stat-trend">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <div className="stat-info">
              <h3>+{newCompaniesCount}</h3>
            </div>
            <div className="stat-trend">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
          </div>
        </section>
        {/* Quick Actions Grid */}
        <section className="actions-section dashboard-surface">
          <h2 className="section-title">
            <FontAwesomeIcon icon={faBolt} className="section-title__icon" />
            Acciones Rápidas
          </h2>
          <div className="actions-grid">
            {quickActions
              .filter((action) => action.allowed)
              .map((action, idx) => (
                <Link
                  key={idx}
                  to={action.path}
                  className="action-card action-card--modern"
                >
                  <span className="action-card__icon-wrap" aria-hidden>
                    <FontAwesomeIcon
                      icon={["fas", action.icon as any]}
                      className="action-card__icon"
                    />
                  </span>
                  <span className="action-card__label">
                    {intl.formatMessage({ id: action.label })}
                  </span>
                </Link>
              ))}
            <a
              href="https://soporte.wembii.com"
              target="_blank"
              rel="noopener noreferrer"
              className="action-card action-card--modern action-card--help"
            >
              <span className="action-card__icon-wrap" aria-hidden>
                <FontAwesomeIcon
                  icon={["fas", "headset"]}
                  className="action-card__icon"
                />
              </span>
              <span className="action-card__label">Centro de ayuda</span>
            </a>
          </div>
        </section>

        {/* Plan Features */}
        <section className="plan-section plan-section--modern dashboard-surface">
          <div className="plan-header">
            <h2 className="section-title">
              <FontAwesomeIcon icon={faGem} className="section-title__icon" />
              Tu Plan
            </h2>
            <div className="plan-badge">
              {companyPlan?.name.split(".")[2]}
            </div>
          </div>
          <div className="features-grid">
            <PlanFeature
              icon={faUserFriends}
              label="Equipo"
              value={`${companyPlan?.rules.teamSize} miembros`}
            />
            <PlanFeature
              icon={faFileAlt}
              label="Formularios"
              value={companyPlan?.rules.webFormsAmount}
            />
            <PlanFeature
              icon={faChartLine}
              label="Informes"
              value={companyPlan?.rules.reportsAmount}
            />
            <PlanFeature
              icon={faClock}
              label="Publicaciones"
              value={
                companyPlan?.rules.allowScheduledPost ? "Activo" : "Inactivo"
              }
            />
            <PlanFeature
              icon={faRobot}
              label="Automatización"
              value={companyPlan?.rules.allowAutomation ? "Activo" : "Inactivo"}
            />
          </div>
          <div className="plan-actions">
            <UpgradePlanButton />
            <a
              href="https://wembii.com/planes"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline-glow btn-outline-glow--modern"
            >
              Comparar Planes
              <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-2" />
            </a>
          </div>
        </section>
      </main>

      <footer className="app-footer">
        <div className="container">
          <div className="footer-content">
            <p className="copyright">
              © 2026 Wembii® SpA
            </p>
            <a
              href="https://soporte.wembii.com"
              target="_blank"
              rel="noopener noreferrer"
              className="help-link"
            >
              <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
              Centro de Ayuda
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const PlanFeature: React.FC<{
  icon: any;
  label: string;
  value: ReactNode;
}> = ({ icon, label, value }) => (
  <div className="feature-card feature-card--modern">
    <span className="feature-card__icon-wrap" aria-hidden>
      <FontAwesomeIcon icon={icon} className="feature-card__icon" />
    </span>
    <div className="feature-card__body">
      <h4>{label}</h4>
      <p>{value}</p>
    </div>
  </div>
);

export default HomeDashboard;
